import { makeAutoObservable, runInAction } from 'mobx';
import { BookingApi } from 'shared/api';
import { bookingSocketService } from 'shared/api/booking/model/socket-booking';
import { CalendarDayType } from 'shared/api/booking/interfaces/calendar-day.type';
import { CalendarCardType } from 'features/day-calendar/types/business-card.type';
import { CalendarDate, ICalendarDate } from './calendar-date';

type DayUpdateData = {
  type: 'full' | 'created' | 'updated' | 'deleted';
  data?: CalendarDayType;
  booking?: unknown;
  bookingId?: string;
};

export class DayCalendarStore {
  selectedDate: ICalendarDate;
  calendarData: CalendarDayType = [];
  selectedCard: { id: string; type: CalendarCardType } | null = null;
  pointId: string | null = null;
  isLoading = false;
  isConnected = false;

  constructor() {
    const now = new Date();
    const nowDate: ICalendarDate = {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
    };
    this.selectedDate = this.loadDateFromStorage() || nowDate;

    makeAutoObservable(this);
  }

  connect = (pointId: string, date: string) => {
    this.disconnect();
    this.pointId = pointId;

    bookingSocketService.connect(pointId, date, this.handleDayUpdate);

    runInAction(() => {
      this.isConnected = bookingSocketService.connected;
    });
  };

  disconnect = () => {
    bookingSocketService.disconnect();
    this.isConnected = false;
  };

  private loadDateFromStorage(): ICalendarDate | null {
    try {
      const saved = localStorage.getItem('selectedDate');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Failed to parse date from storage', e);
      return null;
    }
  }

  initCalendarDay = async () => {
    if (!this.pointId || !this.selectedDate) return;

    runInAction(() => {
      this.isLoading = true;
    });

    const socketData = bookingSocketService.getDayData();
    if (socketData && socketData.length > 0) {
      runInAction(() => {
        this.calendarData = this.sortData(socketData);
        this.isLoading = false;
      });
      return;
    }

    const date = CalendarDate.calendarDateToDate(this.selectedDate);
    try {
      const data = await BookingApi.getCalendarDay({
        date,
        pointId: this.pointId,
      });

      runInAction(() => {
        this.calendarData = this.sortData(data);
        this.isLoading = false;
      });
    } catch (error) {
      console.error('[DayCalendarStore] Error fetching:', error);
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedDate(date: ICalendarDate) {
    this.selectedDate = date;

    if (date) {
      localStorage.setItem('selectedDate', JSON.stringify(date));
    } else {
      localStorage.removeItem('selectedDate');
    }
  }

  setSelectedCard = (id: string, type: CalendarCardType) => {
    this.selectedCard = { id, type };
  };

  clearSelectedCard = () => {
    this.selectedCard = null;
  };

  private handleDayUpdate = (data: DayUpdateData) => {
    if (data.type === 'full' && data.data) {
      runInAction(() => {
        this.calendarData = this.sortData(data.data as CalendarDayType);
      });
    } else {
      this.fetchCalendarDay();
    }
  };

  private fetchCalendarDay = async () => {
    if (!this.pointId || !this.selectedDate) return;

    runInAction(() => {
      this.isLoading = true;
    });

    const date = CalendarDate.calendarDateToDate(this.selectedDate);
    try {
      const data = await BookingApi.getCalendarDay({
        date,
        pointId: this.pointId,
      });

      runInAction(() => {
        this.calendarData = this.sortData(data);
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  private sortData = (data: CalendarDayType): CalendarDayType => {
    return [...data].sort((a, b) => {
      if (a.workDay && !b.workDay) return -1;
      if (!a.workDay && b.workDay) return 1;
      return 0;
    });
  };

  get getSelectedDate(): ICalendarDate {
    const dateString = localStorage.getItem('selectedDate');
    const localDate = dateString
      ? JSON.parse(dateString)
      : CalendarDate.dateToCalendarDate(new Date());

    return this.selectedDate ?? localDate;
  }

  get getSelectedDateAsDate(): Date {
    const calendarDate = this.getSelectedDate;
    return CalendarDate.calendarDateToDate(calendarDate);
  }
}

export const dayCalendarStore = new DayCalendarStore();
